import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, Coins, Play, ArrowRight, Crosshair, Crown, Bot, Globe, BookOpen, CirclePlay } from 'lucide-react';
import { GameVariant, VARIANT_SPECS, ACTIVE_VARIANTS } from '@draughts/shared';
import PuzzlePreviewBoard from '../shared/PuzzlePreviewBoard';
import CorniceDamiera from '../shared/CorniceDamiera';
import { deriveRuleStatements, type RuleCategory } from '../../rules/deriveRules';
import {
  hasOpeningsPage,
  hasCaptureRulesPage,
  buildRulesPath,
  buildGameRulesPath,
  buildOpeningsPath,
  buildCapturePath,
  isRulesLang,
  type RulesLang,
} from '../../seo/routes';
import { variantsPreferredFirst } from '../../rules/order-variants';
import { channelsFor } from '../../content/variant-channels';
import RulesChannelsOverlay from './RulesChannelsOverlay';
import '../shared/sweep-ring.css';
import './Rules.css';

interface RulesPageProps {
  variant: GameVariant;
  onBack: () => void;
  onSelectVariant: (v: GameVariant) => void;
  /** Open the variant's opening tables — only shown where a F.I.D. ballot exists. */
  onOpenOpenings: () => void;
  /** Open the detailed capture rules — only where the precedence is a rule set of its own. */
  onOpenCaptureRules: () => void;
  onPlay: (variant: GameVariant, mode: 'ai' | 'online') => void;
  /**
   * La variante preferita dell'utente, in minuscolo: la sua card sale in cima
   * a «Altre varianti». Arriva come PROP e non dallo store perche' questa
   * pagina la pre-renderizza anche il prerender SSR — vedi RulesPrerenderRoot.
   */
  preferred?: string;
}

const THEMES: { category: RuleCategory; titleKey: string; Icon: typeof ArrowRight }[] = [
  { category: 'movement', titleKey: 'rules.theme.movement', Icon: ArrowRight },
  { category: 'capture', titleKey: 'rules.theme.capture', Icon: Crosshair },
  { category: 'promotion', titleKey: 'rules.theme.promotion', Icon: Crown },
];

export default function RulesPage({
  variant,
  onBack,
  onSelectVariant,
  onOpenOpenings,
  onOpenCaptureRules,
  onPlay,
  preferred,
}: RulesPageProps) {
  const { t, i18n } = useTranslation();
  const [channelsOpen, setChannelsOpen] = useState(false);
  const lang: RulesLang = isRulesLang(i18n.language) ? i18n.language : 'en';

  const spec = VARIANT_SPECS[variant];
  const lower = variant.toLowerCase();
  const name = t(`game.variants.${lower}`);
  const facts = deriveRuleStatements(spec);
  const boardSize = `${spec.board.size}×${spec.board.size}`;
  // La preferita in testa; se e' proprio questa pagina, il filtro l'ha gia'
  // tolta e l'ordine resta quello canonico.
  const otherVariants = variantsPreferredFirst(
    ACTIVE_VARIANTS.filter((v) => v !== variant),
    preferred,
  );
  // Funzione pura su una tabella statica: la chiama anche il prerender SSR,
  // quindi i link finiscono nell'HTML cotto e non servono prop nuove.
  const channels = channelsFor(variant);

  return (
    <div className="rl-page">
      <div className="rl-inner">
        {/* breadcrumb — all'ELENCO delle varianti, non all'indice della sezione:
            l'indice e' due card, e chi torna da una variante vuole la griglia. */}
        <div className="rl-crumb">
          <a
            href={buildGameRulesPath(lang)}
            onClick={(e) => { e.preventDefault(); onBack(); }}
          >
            <span aria-hidden="true">←</span> {t('rules.backToRules')}
          </a>
          <span className="rl-sep">/</span>
          <span className="rl-now">{name}</span>
        </div>

        {/* hero */}
        <div className="rl-card rl-hero rl-hero-3">
          <div className="rl-hero-deco"><div className="rl-hero-king" /></div>
          <div className="rl-hero-center">
            <span className="rl-eyebrow">{t('rules.eyebrow')}</span>
            <h1 className="rl-hero-title">
              <span className="rl-flag" aria-hidden="true">{spec.ui.flagEmoji}</span> {name}
            </h1>
            <p className="rl-hero-sub">{t('rules.heroSubtitle', { variant: name })}</p>
          </div>
          <div className="rl-hero-chip">
            <span className="rl-chip-ico"><LayoutGrid /></span>
            <span className="rl-chip-txt">
              <span className="rl-chip-v">{boardSize}</span>
              <span className="rl-chip-l">{t('rules.glance.board')}</span>
            </span>
          </div>
        </div>

        {/* board + at-a-glance */}
        <div className="rl-row2">
          <div className="rl-card rl-board-card">
            <span className="rl-eyebrow rl-head">{t('rules.startingPosition')}</span>
            <div
              className="rl-board-wrap"
              style={{
                '--rules-sq-dark': spec.ui.colors.darkSquare,
                '--rules-sq-light': spec.ui.colors.lightSquare,
                '--rules-piece-white': spec.ui.colors.whitePiece,
                '--rules-piece-black': spec.ui.colors.blackPiece,
              } as CSSProperties}
            >
              <CorniceDamiera className="rl-cornice">
                <PuzzlePreviewBoard
                  fen={spec.board.initialFen}
                  variant={lower}
                  squareSize={36}
                  className="rl-board-svg"
                  darkSquareClass="rules-sq-dark"
                  lightSquareClass="rules-sq-light"
                  ariaLabel={t('rules.startingPosition') + ' — ' + name}
                />
              </CorniceDamiera>
            </div>
          </div>

          <div className="rl-card rl-glance">
            <span className="rl-eyebrow">{t('rules.glance.title')}</span>
            <div className="rl-rows">
              <div className="rl-stat">
                <span className="rl-lbl"><LayoutGrid /> {t('rules.glance.board')}</span>
                <span className="rl-val">{boardSize}</span>
              </div>
              <div className="rl-stat">
                <span className="rl-lbl"><Coins /> {t('rules.glance.pieces')}</span>
                <span className="rl-val">{spec.board.piecesPerPlayer}</span>
              </div>
              <div className="rl-stat">
                <span className="rl-lbl"><Play /> {t('rules.glance.starts')}</span>
                <span className="rl-val">
                  {spec.board.blackStarts ? t('game.clock.black') : t('game.clock.white')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* how to play */}
        <div className="rl-howto">
          <h2 className="rl-section-head">{t('rules.howToPlay')}</h2>
          <div className="rl-theme-grid">
            {THEMES.map(({ category, titleKey, Icon }) => {
              const items = facts.filter((f) => f.category === category);
              if (items.length === 0) return null;
              return (
                <div key={category} className="rl-card rl-theme">
                  <div className="rl-t-head">
                    <span className="rl-t-ico"><Icon /></span>
                    <span className="rl-t-name">{t(titleKey)}</span>
                  </div>
                  <ul>
                    {items.map((f) => (
                      <li key={f.key}>{t(f.key, f.values)}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>

        {/* Capture rules: where the precedence decides WHICH capture is forced,
            six summary lines are not enough — the deep dive gets its own page.
            Placed before the ballot tables: rules of play first. */}
        {hasCaptureRulesPage(variant) && (
          <a
            className="rl-card rl-op-entry"
            href={buildCapturePath(lang, variant)}
            onClick={(e) => { e.preventDefault(); onOpenCaptureRules(); }}
          >
            {/* Questa e quella qui sotto sono le uniche due card della pagina
                che si premono, e sono fatte come tutte le altre: stesso vetro,
                stesso bordo, stesso raggio. L'anello di luce lo dice dove
                l'hover non arriva mai — cioe' su mobile. E' lo stesso della
                pillola «N giocatori collegati»: maschera e gradiente devono
                stare su due layer separati, percio' e' un elemento vero e non
                uno pseudo-elemento. Vedi shared/sweep-ring.css. */}
            <span className="sweep-ring" aria-hidden="true" />
            <span className="rl-op-entry-ico"><Crosshair /></span>
            <span className="rl-op-entry-txt">
              <span className="rl-op-entry-title">{t('rules.capture.cta')}</span>
              <span className="rl-op-entry-desc">{t('rules.capture.ctaDesc')}</span>
            </span>
            <span className="rl-op-entry-arrow" aria-hidden="true">→</span>
          </a>
        )}

        {/* Opening tables: italian and english draw their first three half-moves
            from an official table, which is a rule of the game, not a detail. */}
        {hasOpeningsPage(variant) && (
          <a
            className="rl-card rl-op-entry"
            href={buildOpeningsPath(lang, variant)}
            onClick={(e) => { e.preventDefault(); onOpenOpenings(); }}
          >
            <span className="sweep-ring" aria-hidden="true" />
            <span className="rl-op-entry-ico"><BookOpen /></span>
            <span className="rl-op-entry-txt">
              <span className="rl-op-entry-title">{t('rules.openings.cta')}</span>
              <span className="rl-op-entry-desc">{t('rules.openings.ctaDesc')}</span>
            </span>
            <span className="rl-op-entry-arrow" aria-hidden="true">→</span>
          </a>
        )}

        {/* play CTAs */}
        <div className="rl-cta-row">
          <button
            type="button"
            className="rl-cta rl-cta-online"
            data-testid="rules-play-online"
            onClick={() => onPlay(variant, 'online')}
          >
            <Globe /> {t('rules.playOnline', 'Play now')}
          </button>
          <button
            type="button"
            className="rl-cta rl-cta-ai"
            data-testid="rules-play-ai"
            onClick={() => onPlay(variant, 'ai')}
          >
            <Bot /> {t('rules.playAI', 'Play vs computer')}
          </button>
        </div>

        {/* I canali stanno in un foglio a parte, non in linea: la pagina regola
            una variante, e una vetrina di partner in mezzo al percorso «leggi le
            regole → gioca» si mette davanti alle due CTA invece che dopo. Il
            bottone c'e' solo dove ci sono canali. */}
        {channels.length > 0 && (
          <button
            type="button"
            className="rl-cta rl-cta-channels"
            data-testid="rules-open-channels"
            onClick={() => setChannelsOpen(true)}
          >
            <CirclePlay /> {t('rules.channels.cta')}
          </button>
        )}

        {/* other variants */}
        <div className="rl-others">
          <h2 className="rl-section-head">
            {t('rules.otherVariants')} <span className="rl-count">({otherVariants.length})</span>
          </h2>
          <div className="rl-var-grid">
            {otherVariants.map((v) => {
              const s = VARIANT_SPECS[v];
              return (
                <a
                  key={v}
                  className="rl-var"
                  href={buildRulesPath(lang, v)}
                  onClick={(e) => { e.preventDefault(); onSelectVariant(v); }}
                >
                  <span className="rl-vflag" aria-hidden="true">{s.ui.flagEmoji}</span>
                  <span className="rl-vtxt">
                    <span className="rl-vname">{t(`game.variants.${v.toLowerCase()}Short`, v.toLowerCase())}</span>
                    <span className="rl-vmeta">{s.board.size}×{s.board.size}</span>
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <RulesChannelsOverlay
        isOpen={channelsOpen}
        onClose={() => setChannelsOpen(false)}
        variant={variant}
        channels={channels}
      />
    </div>
  );
}
